import { useBusinessEntity } from '@zenbusiness/application-commons-business-entity';
import { useExperimentFlag, useExperimentFlagDetail } from '@zenbusiness/application-commons-experiments';
import { gql, useQuery } from '@zenbusiness/application-commons-graphql';
import { useError } from '@zenbusiness/application-commons-logging';

import {
  APPLE_PAY_DPAN_UPDATE_PROMPT,
  ENABLE_BACKUP_CARD_DASHBOARD_BILLING,
  ENABLE_CA_FILING_ACCESS_BANNER,
  WFC_PAST_DUE_BANNER_CTA_EXPERIMENT,
} from '#client/constants/experimentsFlags';

export const BANNERS_QUERY = gql`
  query Banners(
    $businessEntityUuid: UUID!
    $enableBackupCardBanner: Boolean
    $enableCAFilingAccessBanner: Boolean
    $enableUpdatePaymentMethodBanner: Boolean
    $wfcPastDueBannerCtaExperiment: String
  ) {
    banners(
      filter: {
        businessEntityUuid: $businessEntityUuid
        experiment: {
          enableBackupCardBanner: $enableBackupCardBanner
          enableCAFilingAccessBanner: $enableCAFilingAccessBanner
          enableUpdatePaymentMethodBanner: $enableUpdatePaymentMethodBanner
          wfcPastDueBannerCtaExperiment: $wfcPastDueBannerCtaExperiment
        }
      }
    ) {
      title
      description
      type
      imageUrl
      imageSize
      raised
      dismissible
      storageKey
      experiment {
        featureFlagKey
        featureFlagVariation
      }
      primaryCta {
        label
        modalName
        url
      }
      secondaryCta {
        label
        modalName
        url
      }
      clickstream {
        elementName
        ctaElementName
        dynamicProperties
      }
    }
  }
`;

export function useBanners() {
  const { businessEntityUuid } = useBusinessEntity();
  const enableBackupCardBanner = useExperimentFlag(ENABLE_BACKUP_CARD_DASHBOARD_BILLING, false);
  const enableCAFilingAccessBanner = useExperimentFlag(ENABLE_CA_FILING_ACCESS_BANNER, false);
  const enableUpdatePaymentMethodBanner = useExperimentFlag(APPLE_PAY_DPAN_UPDATE_PROMPT, false);
  const { value: wfcPastDueBannerCtaExperiment, reason } = useExperimentFlagDetail(
    WFC_PAST_DUE_BANNER_CTA_EXPERIMENT,
    null,
  );

  const { data, loading, error } = useQuery(BANNERS_QUERY, {
    noThrow: true,
    skip: !businessEntityUuid,
    variables: {
      businessEntityUuid,
      enableBackupCardBanner,
      enableCAFilingAccessBanner,
      enableUpdatePaymentMethodBanner,
      wfcPastDueBannerCtaExperiment,
    },
  });

  useError(error);

  return {
    data: data?.banners || [],
    error,
    inWfcPastDueBannerCtaExperiment: reason?.inExperiment ?? false,
    loading,
  };
}
